import { trackEvent } from "@/lib/analytics-events";

// Generated contract projection. Meaning and compatibility aliases are owned by
// business/ishvaram/data/governance/offer_milestone_events.yaml; the Python
// parity test fails closed if these canonical names drift.
export const OFFER_MILESTONE_EVENTS = {
  offerCardSeen: "offer_card_seen",
  offerCtaSeen: "offer_cta_seen",
  offerCtaTapped: "offer_cta_tapped",
  checkoutStarted: "checkout_started",
} as const;

export type OfferActionMode = "direct_checkout" | "input_collection";

export function offerPresentationId({
  actionMode,
  planId,
  placement,
  presentationScope,
  sourcePath,
}: {
  actionMode: OfferActionMode;
  planId: string;
  placement: string;
  presentationScope?: string;
  sourcePath: string;
}): string {
  const scopedIdentity = presentationScope ? `|${presentationScope}` : "";
  return `${actionMode}|${planId}|${placement}${scopedIdentity}|${sourcePath}`;
}

export interface OfferMilestoneProperties {
  plan_id: string;
  placement: string;
  source_path?: string;
  landing_path?: string;
  reading_id?: string;
  source?: string;
  intent?: string;
  landing_intent?: string;
  price_inr?: number;
  value?: number;
  currency?: string;
  amount_minor?: number;
  is_primary_cta?: boolean;
  presentation_id?: string;
  journey_action_id?: string;
  concern_id?: string;
  journey_slot?: string;
  journey_stage?: string;
  journey_action_kind?: string;
  journey_policy_id?: string;
  journey_policy_version?: number;
  offer_action_mode?: OfferActionMode;
}

type DirectCheckoutMilestoneProperties = OfferMilestoneProperties & {
  offer_action_mode: "direct_checkout";
};

type OfferMilestoneEvent = (typeof OFFER_MILESTONE_EVENTS)[keyof typeof OFFER_MILESTONE_EVENTS];

function trackOfferMilestone(event: OfferMilestoneEvent, props: OfferMilestoneProperties): void {
  trackEvent(event, {
    ...props,
    offer_milestone: event,
    offer_milestone_contract_version: 3,
  });
}

/** Emit through useImpressionWhenVisible(cardRef, threshold=.5, dwellMs=1000). */
export function trackOfferCardSeen(
  props: OfferMilestoneProperties & {
    offer_action_mode: OfferActionMode;
  }
): void {
  trackOfferMilestone(OFFER_MILESTONE_EVENTS.offerCardSeen, props);
}

/** Emit through useImpressionWhenVisible(primaryCtaRef, threshold=.5, dwellMs=1000). */
export function trackOfferCtaSeen(props: DirectCheckoutMilestoneProperties): void {
  trackOfferMilestone(OFFER_MILESTONE_EVENTS.offerCtaSeen, props);
}

/** Emit synchronously on CTA activation, before validation or order creation. */
export function trackOfferCtaTapped(props: DirectCheckoutMilestoneProperties): void {
  trackOfferMilestone(OFFER_MILESTONE_EVENTS.offerCtaTapped, props);
}

/** Emit only after successful order creation, immediately before checkout handoff. */
export function trackOfferCheckoutStarted(props: DirectCheckoutMilestoneProperties): void {
  trackOfferMilestone(OFFER_MILESTONE_EVENTS.checkoutStarted, props);
}
